import { useCallback, useEffect, useMemo } from 'react';
import { callBackend } from '../../../utils/api';
import {
    normalizeMarketStatus,
    normalizePriceSource,
    parseQuoteTimestamp,
} from '../dashboardUtils';
import { buildRiskRequestKey } from '../orchestration/requestRegistry';
import useSingleFlightRequests from './useSingleFlightRequests';
import { waitForInitialMarketData } from '../initialMarketDataReadiness';
import { MARKET_HEALTH_CHANGED_EVENT } from '../../../utils/marketHealth';

export default function useLiveQuotesPolling({
    userUid,
    activeView,
    trackedQuoteIdentities,
    startupReady = true,
    publicationPaused = false,
    initialFetchDebounceMs = 300,
    watchlistQuotePollMs,
    overviewQuotePollMs,
    importApplyInFlightRef,
    liveQuotesRequestIdRef,
    liveQuotesByCogniveIdRef,
    setLiveQuotesByCogniveId,
    setLiveQuotesLastUpdatedAt,
}) {
    const runSingleFlight = useSingleFlightRequests();
    const trackedQuoteSetKey = useMemo(() => {
        const byKey = new Map();
        (Array.isArray(trackedQuoteIdentities) ? trackedQuoteIdentities : []).forEach((item) => {
            const cogniveId = item?.cognive_id == null ? '' : String(item.cognive_id).trim();
            if (!cogniveId) return;
            byKey.set(cogniveId, { cognive_id: cogniveId });
        });
        return JSON.stringify([...byKey.entries()].sort(([left], [right]) => left.localeCompare(right)));
    }, [trackedQuoteIdentities]);
    const stableTrackedQuoteIdentities = useMemo(
        () => JSON.parse(trackedQuoteSetKey).map(([, identity]) => identity),
        [trackedQuoteSetKey],
    );

    const fetchSecurityQuotes = useCallback(async (identities = [], options = {}) => {
        if (!userUid) {
            return null;
        }

        const {
            isCurrentRequest = null,
            stagePublicationState = null,
            expectedWatchlistRevisionId = null,
            expectedWatchlistRevisionSeq = null,
            expectedWatchlistFactorContextId = null,
        } = options;
        const isCurrent = typeof isCurrentRequest === 'function'
            ? isCurrentRequest
            : () => true;

        const cogniveIds = [...new Set(identities.map((item) => (
            item?.cognive_id == null ? '' : String(item.cognive_id).trim()
        )).filter(Boolean))];
        const requestId = ++liveQuotesRequestIdRef.current;
        const isCurrentQuoteRequest = () => (
            isCurrent()
            && liveQuotesRequestIdRef.current === requestId
        );
        const publishQuoteSnapshot = (nextQuoteMap, latestTimestamp) => {
            if (!isCurrentQuoteRequest()) {
                return false;
            }
            const apply = () => {
                liveQuotesByCogniveIdRef.current = nextQuoteMap;
                setLiveQuotesByCogniveId(nextQuoteMap);
                setLiveQuotesLastUpdatedAt(
                    latestTimestamp && !Number.isNaN(latestTimestamp.getTime())
                        ? latestTimestamp
                        : null,
                );
            };
            if (typeof stagePublicationState === 'function') {
                stagePublicationState(apply, isCurrentQuoteRequest);
            } else {
                apply();
            }
            return true;
        };

        if (!cogniveIds.length) {
            const publicationStateStaged = publishQuoteSnapshot({}, null);
            return {
                quotes: [],
                latest_updated_at: null,
                publicationStateStaged,
            };
        }

        const singleFlightKey = buildRiskRequestKey([
            'fetch_security_quotes',
            userUid,
            [...cogniveIds].sort(),
            expectedWatchlistRevisionId,
            expectedWatchlistRevisionSeq,
            expectedWatchlistFactorContextId,
        ]);
        try {
            const fetchSnapshot = () => runSingleFlight(singleFlightKey, () =>
                callBackend('fetch_security_quotes', 'POST', {
                    cognive_ids: cogniveIds,
                    ...(expectedWatchlistRevisionId ? {
                        expected_watchlist_revision_id: expectedWatchlistRevisionId,
                        expected_watchlist_revision_seq: expectedWatchlistRevisionSeq,
                        expected_watchlist_factor_context_id: expectedWatchlistFactorContextId,
                    } : {}),
                }),
            );
            const resp = expectedWatchlistRevisionId
                ? await waitForInitialMarketData(fetchSnapshot, { isCurrent: isCurrentQuoteRequest })
                : await fetchSnapshot();
            if (!isCurrentQuoteRequest()) {
                return null;
            }

            const nextQuoteMap = {};
            let latestTimestamp = null;
            (resp?.quotes || []).forEach((quote) => {
                const cogniveId = quote?.cognive_id == null
                    ? ''
                    : String(quote.cognive_id).trim();
                if (!cogniveId) return;
                const parsedQuoteTimestamp = parseQuoteTimestamp(
                    quote?.quote_timestamp ?? quote?.updated_at,
                );
                const parsedUpdatedAt = parseQuoteTimestamp(quote?.updated_at);
                const nextQuoteTimestampMs = parsedQuoteTimestamp ? parsedQuoteTimestamp.getTime() : null;
                const nextUpdatedAtMs = parsedUpdatedAt ? parsedUpdatedAt.getTime() : null;
                const rawPriceSource = normalizePriceSource(quote?.price_source);
                const rawMarketStatus = normalizeMarketStatus(quote?.market_status);
                const timestampForLatest = parsedQuoteTimestamp ?? parsedUpdatedAt;
                if (
                    timestampForLatest &&
                    (!latestTimestamp || timestampForLatest.getTime() > latestTimestamp.getTime())
                ) {
                    latestTimestamp = timestampForLatest;
                }
                const normalizedQuote = {
                    ...quote,
                    price_source: rawPriceSource ?? quote?.price_source ?? null,
                    market_status: rawMarketStatus ?? quote?.market_status ?? null,
                    quote_timestamp_ms: nextQuoteTimestampMs,
                    updated_at_ms: nextUpdatedAtMs,
                };
                nextQuoteMap[cogniveId] = normalizedQuote;
            });
            const publicationStateStaged = publishQuoteSnapshot(nextQuoteMap, latestTimestamp);
            return {
                ...resp,
                publicationStateStaged,
            };
        } catch (err) {
            if (expectedWatchlistRevisionId) throw err;
            if (isCurrentQuoteRequest()) {
                console.error('Error fetching security quotes:', err);
            }
            return {
                quotes: [],
                latest_updated_at: null,
                publicationStateStaged: false,
            };
        }
    }, [
        userUid,
        liveQuotesRequestIdRef,
        liveQuotesByCogniveIdRef,
        runSingleFlight,
        setLiveQuotesByCogniveId,
        setLiveQuotesLastUpdatedAt,
    ]);

    useEffect(() => {
        if (!userUid) return;
        if (activeView !== 'watchlist' && activeView !== 'overview') return;
        if (!startupReady) return;
        if (publicationPaused) return;
        if (!stableTrackedQuoteIdentities.length) {
            setLiveQuotesByCogniveId({});
            setLiveQuotesLastUpdatedAt(null);
            return;
        }

        const intervalMs = activeView === 'watchlist'
            ? watchlistQuotePollMs
            : overviewQuotePollMs;
        let cancelled = false;
        let timeoutId = null;
        let polling = false;

        const scheduleNext = () => {
            if (cancelled) return;
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = window.setTimeout(runPoll, intervalMs);
        };

        const runPoll = async () => {
            if (cancelled || polling) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                scheduleNext();
                return;
            }
            if (importApplyInFlightRef.current) {
                scheduleNext();
                return;
            }

            polling = true;
            await fetchSecurityQuotes(stableTrackedQuoteIdentities, {
                isCurrentRequest: () => !cancelled,
            });
            polling = false;
            scheduleNext();
        };

        const handleVisibilityChange = () => {
            if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
                return;
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            void runPoll();
        };
        const handleMarketHealthChange = (event) => {
            if (event.detail?.user_uid !== userUid) return;
            handleVisibilityChange();
        };

        if (initialFetchDebounceMs > 0) {
            timeoutId = window.setTimeout(runPoll, initialFetchDebounceMs);
        } else {
            void runPoll();
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener(MARKET_HEALTH_CHANGED_EVENT, handleMarketHealthChange);

        return () => {
            cancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener(MARKET_HEALTH_CHANGED_EVENT, handleMarketHealthChange);
        };
    }, [
        userUid,
        activeView,
        stableTrackedQuoteIdentities,
        startupReady,
        publicationPaused,
        initialFetchDebounceMs,
        fetchSecurityQuotes,
        overviewQuotePollMs,
        watchlistQuotePollMs,
        importApplyInFlightRef,
        setLiveQuotesByCogniveId,
        setLiveQuotesLastUpdatedAt,
    ]);

    return { fetchSecurityQuotes };
}
